<template>
  <form class="cognito-login" @submit.prevent="submit" novalidate>
    <h2 class="cognito-login__title">{{ titles[step] }}</h2>
    <p v-if="info" class="cognito-login__notice" role="status">{{ info }}</p>

    <template v-if="step === 'signin'">
      <label class="cognito-login__field"><span>Email</span>
        <input ref="first" v-model.trim="email" type="email" name="username" autocomplete="username" autocapitalize="none" spellcheck="false" :disabled="busy" required />
      </label>
      <label class="cognito-login__field"><span>Password</span>
        <input v-model="password" type="password" name="password" autocomplete="current-password" :disabled="busy" required />
      </label>
    </template>

    <template v-else-if="step === 'newpw'">
      <p class="cognito-login__hint">Choose a new password (at least 8 characters with upper- and lower-case letters, a number and a symbol).</p>
      <label class="cognito-login__field"><span>New password</span>
        <input ref="first" v-model="newPassword" type="password" autocomplete="new-password" :disabled="busy" required />
      </label>
    </template>

    <template v-else-if="step === 'setup'">
      <p class="cognito-login__hint">Scan this code with an authenticator app (Google Authenticator, 1Password, Authy…), then enter the 6-digit code it shows.</p>
      <img v-if="qr" :src="qr" alt="Authenticator QR code" class="cognito-login__qr" width="180" height="180" />
      <details class="cognito-login__secret"><summary>Can't scan? Enter this key instead</summary><code>{{ secret }}</code></details>
      <label class="cognito-login__field"><span>6-digit code</span>
        <input ref="first" v-model.trim="code" inputmode="numeric" autocomplete="one-time-code" maxlength="6" :disabled="busy" required />
      </label>
    </template>

    <template v-else-if="step === 'mfa'">
      <p class="cognito-login__hint">Enter the 6-digit code from your authenticator app.</p>
      <label class="cognito-login__field"><span>6-digit code</span>
        <input ref="first" v-model.trim="code" inputmode="numeric" autocomplete="one-time-code" maxlength="6" :disabled="busy" required />
      </label>
    </template>

    <template v-else-if="step === 'forgot'">
      <p class="cognito-login__hint">We'll email you a verification code.</p>
      <label class="cognito-login__field"><span>Email</span>
        <input ref="first" v-model.trim="email" type="email" autocomplete="username" :disabled="busy" required />
      </label>
    </template>

    <template v-else-if="step === 'reset'">
      <label class="cognito-login__field"><span>Code from the email</span>
        <input ref="first" v-model.trim="code" inputmode="numeric" autocomplete="one-time-code" :disabled="busy" required />
      </label>
      <label class="cognito-login__field"><span>New password</span>
        <input v-model="newPassword" type="password" autocomplete="new-password" :disabled="busy" required />
      </label>
    </template>

    <p v-if="error" class="cognito-login__error" role="alert">{{ error }}</p>
    <button type="submit" class="cognito-login__submit" :disabled="busy">{{ busy ? 'One moment…' : buttons[step] }}</button>
    <div class="cognito-login__links">
      <button v-if="step === 'signin'" type="button" class="cognito-login__link" @click="go('forgot')">Forgot your password?</button>
      <button v-else type="button" class="cognito-login__link" @click="restart">Back to sign in</button>
    </div>
  </form>
</template>

<script setup lang="ts">
// Embedded Cognito sign-in (VITE_COGNITO_EMBEDDED_LOGIN=true): the whole flow — password, first-login new password,
// authenticator (TOTP) enrolment with a QR code, TOTP challenge, forgotten password — stays on the app's own login
// page instead of redirecting to the Cognito Hosted UI. Same pool, same app client (SRP), same tokens.
import { nextTick, onMounted, ref } from 'vue';
import { Auth } from 'aws-amplify';
import QRCode from 'qrcode';

type Step = 'signin' | 'newpw' | 'setup' | 'mfa' | 'forgot' | 'reset';
const props = defineProps<{ issuer?: string; notice?: string | null }>();
const emit = defineEmits<{ (e: 'signed-in', user: unknown): void }>();

const titles: Record<Step, string> = { signin: 'Sign in', newpw: 'Set your password', setup: 'Set up two-step sign-in', mfa: 'Two-step sign-in', forgot: 'Reset your password', reset: 'Reset your password' };
const buttons: Record<Step, string> = { signin: 'Sign in', newpw: 'Save password', setup: 'Verify and continue', mfa: 'Verify', forgot: 'Send code', reset: 'Save new password' };

const step = ref<Step>('signin');
const email = ref('');
const password = ref('');
const newPassword = ref('');
const code = ref('');
const secret = ref('');
const qr = ref('');
const error = ref<string | null>(null);
const info = ref<string | null>(props.notice ?? null);
const busy = ref(false);
const first = ref<HTMLInputElement | null>(null);
let cognitoUser: any = null;

const focus = () => nextTick(() => first.value?.focus());
onMounted(focus);

function go(s: Step) { step.value = s; error.value = null; code.value = ''; focus(); }
function restart() { cognitoUser = null; password.value = ''; newPassword.value = ''; info.value = null; go('signin'); }

function message(e: any): string {
  const c = e?.code || e?.name || '';
  if (c === 'NotAuthorizedException' || c === 'UserNotFoundException') return /attempts exceeded/i.test(e?.message || '') ? 'Too many attempts — wait a few minutes and try again.' : 'Incorrect email or password.';
  if (c === 'CodeMismatchException' || c === 'EnableSoftwareTokenMFAException') return "That code didn't match — check the app and try again.";
  if (c === 'ExpiredCodeException') return 'That code has expired — request a new one.';
  if (c === 'InvalidPasswordException') return e.message?.replace(/^Password does not conform to policy: /, '') || 'That password is not strong enough.';
  if (c === 'LimitExceededException' || c === 'TooManyRequestsException') return 'Too many attempts — wait a few minutes and try again.';
  return e?.message || 'Sign-in failed. Please try again.';
}

/** Route the next Cognito challenge to its step; no challenge ⇒ signed in. */
async function next(u: any) {
  cognitoUser = u;
  switch (u?.challengeName) {
    case 'NEW_PASSWORD_REQUIRED': go('newpw'); return;
    case 'MFA_SETUP': {
      secret.value = await Auth.setupTOTP(u);
      const label = encodeURIComponent(`${props.issuer || 'Bridge'}:${email.value}`);
      qr.value = await QRCode.toDataURL(`otpauth://totp/${label}?secret=${secret.value}&issuer=${encodeURIComponent(props.issuer || 'Bridge')}`, { margin: 1, width: 360 });
      go('setup'); return;
    }
    case 'SOFTWARE_TOKEN_MFA': go('mfa'); return;
    case undefined: case null: case '':
      emit('signed-in', await Auth.currentAuthenticatedUser({ bypassCache: true })); return;
    default: throw new Error(`Unsupported sign-in step (${u.challengeName}).`);
  }
}

async function submit() {
  if (busy.value) return;
  error.value = null;
  busy.value = true;
  try {
    if (step.value === 'signin') {
      if (!email.value || !password.value) throw new Error('Enter your email and password.');
      await next(await Auth.signIn(email.value, password.value));
    } else if (step.value === 'newpw') {
      await next(await Auth.completeNewPassword(cognitoUser, newPassword.value));
    } else if (step.value === 'setup') {
      await Auth.verifyTotpToken(cognitoUser, code.value);
      try { await Auth.setPreferredMFA(cognitoUser, 'TOTP'); } catch { /* the pool requires TOTP anyway */ }
      emit('signed-in', await Auth.currentAuthenticatedUser({ bypassCache: true }));
    } else if (step.value === 'mfa') {
      await next(await Auth.confirmSignIn(cognitoUser, code.value, 'SOFTWARE_TOKEN_MFA'));
    } else if (step.value === 'forgot') {
      if (!email.value) throw new Error('Enter your email.');
      await Auth.forgotPassword(email.value);
      info.value = `If ${email.value} has an account, a code is on its way.`;
      go('reset');
    } else if (step.value === 'reset') {
      await Auth.forgotPasswordSubmit(email.value, code.value, newPassword.value);
      info.value = 'Password changed — sign in with the new one.';
      password.value = ''; newPassword.value = '';
      go('signin');
    }
  } catch (e) {
    error.value = message(e);
  } finally {
    busy.value = false;
  }
}
</script>

<style scoped>
.cognito-login { width: 100%; max-width: 340px; display: flex; flex-direction: column; gap: 12px; text-align: left; font-family: var(--font-family-base, Inter, system-ui, sans-serif); }
.cognito-login__title { margin: 4px 0 2px; font: 500 22px/1.2 var(--font-display, inherit); letter-spacing: -0.4px; color: #131311; text-align: center; }
.cognito-login__hint { margin: 0; font-size: 14px; line-height: 1.45; color: #48545f; }
.cognito-login__field { display: flex; flex-direction: column; gap: 5px; font-size: 13px; font-weight: 500; color: #48545f; }
.cognito-login__field input { padding: 11px 13px; border: 1px solid #d8dee5; border-radius: 10px; font-size: 16px; color: #1a222c; background: #fff; }
.cognito-login__field input:focus { outline: 2px solid var(--login-accent, var(--accent, #2b6d94)); outline-offset: 1px; border-color: transparent; }
.cognito-login__qr { align-self: center; border: 1px solid #e6ebf0; border-radius: 12px; padding: 6px; background: #fff; }
.cognito-login__secret { font-size: 12px; color: #48545f; }
.cognito-login__secret code { display: block; margin-top: 6px; word-break: break-all; font-size: 13px; background: #f6f8fa; padding: 6px 8px; border-radius: 6px; }
.cognito-login__error { margin: 0; color: #b83a2f; background: #f7e1de; border-radius: 8px; padding: 8px 10px; font-size: 14px; }
.cognito-login__notice { margin: 0; color: #48545f; background: #f6f8fa; border-radius: 8px; padding: 8px 10px; font-size: 14px; }
.cognito-login__submit { margin-top: 4px; background: var(--login-accent, var(--accent, #2b6d94)); color: #fff; border: none; padding: 13px 20px; border-radius: 30px; font: 500 15px/1 inherit; letter-spacing: .5px; cursor: pointer; }
.cognito-login__submit:hover:not(:disabled) { filter: brightness(0.92); }
.cognito-login__submit:disabled { opacity: .6; cursor: default; }
.cognito-login__links { display: flex; justify-content: center; }
.cognito-login__link { background: none; border: none; color: var(--login-accent, var(--accent, #2b6d94)); font-size: 13px; cursor: pointer; padding: 4px; }
</style>
